import { useState } from "react";
import { Card, CardContent } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Pencil, Trash2, Check, X, Loader2 } from "lucide-react";
import {useCreateTaskCommentMutation,
  useDeleteTaskCommentMutation,
  useGetTaskByIdQuery,
  useUpdateTaskCommentMutation } from "@/services/TasksApi";
import { useSelector } from "react-redux";
import { RootState } from "@/app/store";
import { Comment } from "@/app/types/tasks";
import { useGetStaffListQuery } from '@/services/StaffsApi';

// Maximum comment length constant
const MAX_COMMENT_LENGTH = 350;

interface CommentSectionProps {
  ticketId: number;
  access:string
}

export default function CommentSection({ ticketId,access }: CommentSectionProps) {
  const {
    data,
    refetch,
    isLoading: isFetchingComments,
  } = useGetTaskByIdQuery(ticketId, { skip: !ticketId });
  const { userInfo } = useSelector((state: RootState) => state.auth);
  const { data: staffData } = useGetStaffListQuery({ query: "" });

  const [addComment, { isLoading: isAdding }] = useCreateTaskCommentMutation();
  const [deleteComment] = useDeleteTaskCommentMutation();
  const [updateComment] = useUpdateTaskCommentMutation();

  const [newComment, setNewComment] = useState("");
  const [editingComment, setEditingComment] = useState<number | null>(null);
  const [editText, setEditText] = useState("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Track which comment is being deleted or updated
  const [updatingCommentId, setUpdatingCommentId] = useState<number | null>(
    null
  );
  const [deletingCommentId, setDeletingCommentId] = useState<number | null>(
    null
  );

  const comments = data?.comments || [];

  // Function to normalize text by removing leading spaces and consecutive spaces
  const normalizeText = (text: string): string => {
    // Remove leading spaces and reduce multiple spaces to a single space
    return text.trimStart().replace(/\s+/g, ' ');
  };

  const validateComment = (text: string): boolean => {
    // Don't allow empty comments or comments that are just spaces
    if (!text.trim()) {
      setErrorMessage("Comment cannot be empty");
      return false;
    }
    
    // Check comment length
    if (text.length > MAX_COMMENT_LENGTH) {
      setErrorMessage(`Comment cannot exceed ${MAX_COMMENT_LENGTH} characters`);
      return false;
    }
    
    return true;
  };

  const handleAddComment = async () => {
    const normalizedComment = normalizeText(newComment);
    
    if (!validateComment(normalizedComment)) return;
    
    setErrorMessage(null);
    await addComment({ taskId: ticketId, comment: normalizedComment });
    setNewComment("");
    refetch();
  };

  const handleDeleteComment = async (id: number) => {
    setDeletingCommentId(id);
    await deleteComment({ commentId: id });
    setDeletingCommentId(null);
    refetch();
  };

  const handleEditComment = (comment: Comment) => {
    setEditingComment(comment.id);
    setEditText(comment.comment);
    setErrorMessage(null);
  };

  const handleUpdateComment = async (id: number) => {
    const normalizedComment = normalizeText(editText);
    
    if (!validateComment(normalizedComment)) return;
    
    setErrorMessage(null);
    setUpdatingCommentId(id);
    await updateComment({
      commentId: id,
      comment: normalizedComment,
      staffId: userInfo?.id!,
    });
    setUpdatingCommentId(null);
    setEditingComment(null);
    refetch();
  };


  const handleCommentChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    isEdit: boolean = false
  ) => {
    let value = e.target.value;

    // Prevent starting with space
    if (value.length === 1 && value[0] === " ") {
      setErrorMessage("Comment cannot start with a space");
      return;
    }

    // Prevent multiple consecutive spaces
    if (/\s{2,}/.test(value)) {
      setErrorMessage("Only one space is allowed between words");
      return;
    }

    if (isEdit) {
      setEditText(value);
    } else {
      setNewComment(value);
    }

    // Reset error if valid
    setErrorMessage(null);
  };

  return (
    <div className="mt-4">
      <h2 className="text-lg font-bold mb-2">Comments</h2>

      {isFetchingComments ? (
        <div className="flex justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-gray-500" />
        </div>
      ) : (
        <div className="space-y-2">
          {comments.map((comment) => (
            <Card
              key={comment.id}
              className="p-2 flex justify-between bg-secondary items-center"
            >
              <CardContent className="flex-1 p-0">
                {editingComment === comment.id ? (
                  <div>
                    <input
                      type="text"
                      value={editText}
                      onChange={(e) => handleCommentChange(e, true)}
                      className={`border p-2 rounded w-full ${editText.length > MAX_COMMENT_LENGTH ? "border-red-500" : ""}
                         ${errorMessage ? "border-red-500" : ""}
                        `}
                      disabled={updatingCommentId === comment.id}
                    />
                    <div className="text-xs flex justify-between mt-1">
                      <span
                        className={
                          editText.length > MAX_COMMENT_LENGTH
                            ? "text-red-500"
                            : "text-gray-500"
                        }
                      >
                        {editText.length}/{MAX_COMMENT_LENGTH}
                      </span>
                      {errorMessage && (
                        <span className="text-red-500">{errorMessage}</span>
                      )}
                    </div>
                  </div>
                ) : (
                  <p className="capitalize text-black text-sm break-all">
                    {comment.comment}
                  </p>
                )}
                <p className="text-xs text-gray-500 w-full justify-end font-bold capitalize">
                  {
                    staffData?.find(
                      (staff) => staff.value === comment.created_by
                    )?.label
                  }{" "}
                  -{" "}
                  {(() => {
                    const date = new Date(comment.created_at + "Z");
                    const options: Intl.DateTimeFormatOptions = {
                      timeZone: "Asia/Karachi",
                      year: "numeric",
                      day: "2-digit",
                      month: "2-digit",
                      hour: "2-digit",
                      minute: "2-digit",
                      hour12: true,
                    };

                    const [datePart, timePart] = date
                      .toLocaleString("en-PK", options)
                      .split(", ");
                    const [month, day, year] = datePart.split("/");

                    return `${day}-${year}-${month}, ${timePart}`;
                  })()}
                </p>
              </CardContent>

              {access !== "read" && comment.created_by === userInfo?.id && (
                <div className="flex gap-2 h-full">
                  {editingComment === comment.id ? (
                    <div className="flex gap-2 h-full flex-grow">
                      <Button
                        variant="ghost"
                        className="mb-8"
                        size="icon"
                        onClick={() => handleUpdateComment(comment.id)}
                        disabled={
                          updatingCommentId === comment.id ||
                          editText.length > MAX_COMMENT_LENGTH ||
                          !editText.trim()
                        }
                      >
                        {updatingCommentId === comment.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Check className="w-4 h-4 text-green-500" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="mb-8"
                        onClick={() => setEditingComment(null)}
                        disabled={updatingCommentId === comment.id}
                      >
                        <X className="w-4 h-4 text-gray-500" />
                      </Button>
                    </div>
                  ) : (
                    <>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleEditComment(comment)}
                        disabled={updatingCommentId !== null}
                      >
                        <Pencil className="w-4 h-4 text-blue-500" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeleteComment(comment.id)}
                        disabled={deletingCommentId === comment.id}
                      >
                        {deletingCommentId === comment.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4 text-red-500" />
                        )}
                      </Button>
                    </>
                  )}
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      {access !== "read" && (
        <div className="mt-4">
          <div className="flex gap-2">
            <div className="flex-1">
              <input
                type="text"
                value={newComment}
                onChange={(e) => handleCommentChange(e)}
                className={`border p-2 rounded w-full ${newComment.length > MAX_COMMENT_LENGTH ? "border-red-500" : ""}      
                  ${errorMessage ? "border-red-500" : ""}
                  `}
                placeholder="Add a comment..."
                disabled={isAdding}
              />
              <div className="text-xs flex justify-between mt-1">
                <span
                  className={
                    newComment.length > MAX_COMMENT_LENGTH
                      ? "text-red-500"
                      : "text-gray-500"
                  }
                >
                  {newComment.length}/{MAX_COMMENT_LENGTH}
                </span>
                {errorMessage && (
                  <span className="text-red-500">{errorMessage}</span>
                )}
              </div>
            </div>
            <Button
              onClick={handleAddComment}
              disabled={
                isAdding ||
                newComment.length > MAX_COMMENT_LENGTH ||
                !newComment.trim()
              }
            >
              {isAdding ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <i className="fa-regular fa-floppy-disk text-base px-1 "></i>
              )}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
